import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import BrokenImageRoundedIcon from '@mui/icons-material/BrokenImageRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import {
  identityVerificationDashboardApi,
  type DedupPolicyTier,
  type IdentityLookupRow,
  type IdentityLookupUploadedImage,
  type MitekImageProcessing,
  type MitekJudgement,
  type MitekVerificationCheck,
} from '../api/identityVerificationDashboardApi';
import { messageFromIdentityVerificationHttpError } from '../lib/identityVerificationHttpErrorMessage';
import {
  identitySessionStatusColor,
  monoValueSx,
  VERIFICATION_GRID_SX,
  VerificationDetailField,
  verificationSubsectionHeaderSx,
  verificationSubsectionPaperSx,
} from '../components/application/verificationTabUi';
import { BiometricMatchComparisonPanel } from '../components/application/BiometricMatchComparisonPanel';
import { GEMINI_GRADIENT } from '../theme';
import { isObjectStorageKind } from '../lib/storageKind';

function judgementColor(theme: Theme, group: MitekJudgement) {
  switch (group) {
    case 'AUTHENTIC':
      return theme.palette.success.main;
    case 'FAILED':
      return theme.palette.error.main;
    case 'UNDETERMINED':
      return theme.palette.warning.main;
    case 'OTHER':
    default:
      return alpha(theme.palette.text.secondary, 0.55);
  }
}

function judgementChipColor(group: MitekJudgement): 'success' | 'error' | 'warning' | 'default' {
  switch (group) {
    case 'AUTHENTIC':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'UNDETERMINED':
      return 'warning';
    case 'OTHER':
    default:
      return 'default';
  }
}

function judgementIcon(group: MitekJudgement) {
  switch (group) {
    case 'AUTHENTIC':
      return <CheckCircleRoundedIcon fontSize="small" />;
    case 'FAILED':
      return <CancelRoundedIcon fontSize="small" />;
    case 'UNDETERMINED':
      return <InfoOutlinedIcon fontSize="small" />;
    case 'OTHER':
    default:
      return <HelpOutlineRoundedIcon fontSize="small" />;
  }
}

function probabilityToPercent(probability: number | null): string {
  if (probability == null) return '—';
  if (probability > 1) return `${(probability / 10).toFixed(1)}%`;
  return `${(probability * 100).toFixed(1)}%`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function VerificationsTable({
  checks,
  variant = 'lookup',
}: {
  checks: MitekVerificationCheck[];
  variant?: 'lookup' | 'applicationDetail';
}) {
  const theme = useTheme();
  const isApplicationDetail = variant === 'applicationDetail';
  if (checks.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: isApplicationDetail ? 0 : 1 }}>
        Mitek dossier did not include a `findings.verifications` array.
      </Alert>
    );
  }
  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: isApplicationDetail ? 'fixed' : 'auto',
          '& .MuiTableCell-root': { fontSize: '0.82rem', py: 1, verticalAlign: 'top' },
          '& .MuiTableHead-root .MuiTableCell-root': {
            fontWeight: 700,
            color: 'text.secondary',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: isApplicationDetail ? '14%' : 130 }}>Status</TableCell>
          <TableCell sx={{ width: isApplicationDetail ? '22%' : undefined }}>Verification</TableCell>
          <TableCell sx={{ width: isApplicationDetail ? '12%' : undefined }}>Type</TableCell>
          <TableCell align="right" sx={{ width: isApplicationDetail ? '12%' : undefined }}>
            Probability
          </TableCell>
          <TableCell sx={{ width: isApplicationDetail ? '40%' : undefined }}>Notifications (values missed)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {checks.map((c, idx) => (
          <TableRow
            key={`${c.name}-${c.verificationType ?? 'na'}-${idx}`}
            sx={{
              bgcolor: alpha(judgementColor(theme, c.judgementGroup), 0.04),
            }}
          >
            <TableCell>
              <Chip
                size="small"
                label={c.judgement || 'UNKNOWN'}
                color={judgementChipColor(c.judgementGroup)}
                variant={c.judgementGroup === 'OTHER' ? 'outlined' : 'filled'}
                icon={judgementIcon(c.judgementGroup)}
                sx={{ fontWeight: 700 }}
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {c.name}
              </Typography>
              {c.documentId ? (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  documentId {c.documentId}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>
              <Typography variant="body2">{c.verificationType ?? '—'}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {probabilityToPercent(c.probability)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                raw {c.probability ?? '—'}
              </Typography>
            </TableCell>
            <TableCell>
              {Object.keys(c.notifications).length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {Object.entries(c.notifications).map(([code, msg]) => (
                    <Box key={code}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {code}:
                      </Typography>{' '}
                      <Typography variant="caption" color="text.secondary">
                        {msg}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </Box>
  );
}

function ExtractedFieldsTable({
  fields,
  variant = 'lookup',
}: {
  fields: Array<{ key: string; value: string }>;
  variant?: 'lookup' | 'applicationDetail';
}) {
  const theme = useTheme();
  if (fields.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No OCR fields extracted.
      </Typography>
    );
  }
  if (variant === 'applicationDetail') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 1.25,
          width: '100%',
        }}
      >
        {fields.map((f) => (
          <Box
            key={f.key}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              minWidth: 0,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, display: 'block', mb: 0.35, wordBreak: 'break-word' }}
            >
              {f.key}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {f.value || '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Table size="small" sx={{ width: '100%', '& .MuiTableCell-root': { fontSize: '0.78rem', py: 0.7 } }}>
      <TableBody>
        {fields.map((f) => (
          <TableRow key={f.key}>
            <TableCell sx={{ fontWeight: 700, width: '40%', wordBreak: 'break-word' }}>{f.key}</TableCell>
            <TableCell sx={{ wordBreak: 'break-word' }}>{f.value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function MitekDossierPanels({
  dossier,
  row,
  usedAuditIds,
  setZoom,
  variant,
}: {
  dossier: NonNullable<IdentityLookupRow['dossier']>;
  row: IdentityLookupRow;
  usedAuditIds: Set<string>;
  setZoom: (v: {
    image: IdentityLookupUploadedImage;
    url: string;
    contentType: string;
  } | null) => void;
  variant: 'lookup' | 'applicationDetail';
}) {
  const theme = useTheme();
  const isApplicationDetail = variant === 'applicationDetail';

  const verificationsPanel = (
    <>
      <Box sx={verificationSubsectionHeaderSx(theme)}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Verifications
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Values compared (PASS) and notifications for values missed
        </Typography>
      </Box>
      <Box sx={{ p: 2, flex: 1, minWidth: 0 }}>
        <VerificationsTable checks={dossier.verifications} variant={variant} />
      </Box>
    </>
  );

  const ocrPanel = (
    <>
      <Box sx={verificationSubsectionHeaderSx(theme)}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Extracted OCR
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Fields read from the document image
        </Typography>
      </Box>
      <Box sx={{ p: 2, flex: 1, minWidth: 0 }}>
        <ExtractedFieldsTable fields={dossier.extractedSummary} variant={variant} />
      </Box>
    </>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Mitek dossier · {dossier.dossierId ?? 'no dossier id'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dossier.version ?? 'v?'} · {formatDateTime(dossier.createdDateTime)} · processingStatus{' '}
          {dossier.overallProcessingStatus ?? '—'}
        </Typography>
      </Stack>

      {isApplicationDetail ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', xl: 'row' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ flex: { xl: '1 1 60%' }, minWidth: 0 }}>
            <Box sx={{ ...verificationSubsectionPaperSx(theme), height: '100%' }}>{verificationsPanel}</Box>
          </Box>
          <Box sx={{ flex: { xl: '1 1 40%' }, minWidth: 0 }}>
            <Box sx={{ ...verificationSubsectionPaperSx(theme), height: '100%' }}>{ocrPanel}</Box>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Verifications · values compared (PASS) and values missed (notifications)
                </Typography>
                <VerificationsTable checks={dossier.verifications} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Extracted OCR (values compared)
                </Typography>
                <ExtractedFieldsTable fields={dossier.extractedSummary} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {dossier.images.length > 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, ...(isApplicationDetail ? { boxShadow: theme.shadows[1] } : {}) }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: isApplicationDetail ? 'primary.main' : undefined }}>
              Per-image processing
            </Typography>
            <Stack spacing={1}>
              {dossier.images.map((img, idx) => {
                const failed =
                  (img.processingStatus ?? '').toLowerCase() === 'failed' ||
                  Object.keys(img.processingReasons).length > 0;
                const audit = pickAuditForMitekImage(img, row.uploadedImages, usedAuditIds);
                if (audit) usedAuditIds.add(audit.id);
                return (
                  <Box
                    key={`${img.imageId ?? 'img'}-${idx}`}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: failed ? 'warning.light' : 'divider',
                      bgcolor: failed ? alpha(theme.palette.warning.main, 0.06) : 'background.default',
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {audit ? (
                        <UploadedImagePreview
                          image={audit}
                          size={96}
                          onClick={(url, contentType) => setZoom({ image: audit, url, contentType })}
                        />
                      ) : (
                        <Tooltip
                          title="No matching upload audit row — Mitek dossier reports this image but it was not stored in identity_verification_uploaded_images."
                          arrow
                        >
                          <Box
                            sx={{
                              width: 96,
                              height: 96,
                              borderRadius: 1.5,
                              border: 1,
                              borderStyle: 'dashed',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              bgcolor: alpha(theme.palette.text.primary, 0.03),
                            }}
                          >
                            <BrokenImageRoundedIcon fontSize="small" color="disabled" />
                          </Box>
                        </Tooltip>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={img.processingStatus ?? '—'}
                            color={failed ? 'warning' : 'success'}
                            sx={{ fontWeight: 700 }}
                          />
                          {img.classification?.imageType ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={img.classification.imageType}
                              sx={{ fontWeight: 600 }}
                            />
                          ) : null}
                          {audit ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                audit.image_type === 'selfie'
                                  ? 'primary'
                                  : audit.image_type === 'id_front'
                                    ? 'success'
                                    : 'warning'
                              }
                              label={`${audit.image_type} #${audit.sequence_index}`}
                              sx={{ fontWeight: 600 }}
                            />
                          ) : null}
                          {img.classification?.mdsid ? (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              {img.classification.mdsid}
                            </Typography>
                          ) : null}
                        </Stack>
                        {Object.keys(img.processingReasons).length > 0 ? (
                          <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                            {Object.entries(img.processingReasons).map(([code, msg]) => (
                              <Typography key={code} variant="caption" color="text.secondary">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  {code}:
                                </Box>{' '}
                                {msg}
                              </Typography>
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function tierDetailText(tier: DedupPolicyTier): string {
  const cfg = tier.logic_config ?? {};
  const parts: string[] = [];
  if (typeof cfg.enabled === 'boolean') parts.push(`enabled=${cfg.enabled}`);
  if (typeof cfg.biometric_threshold === 'number') {
    parts.push(`biometric_threshold=${cfg.biometric_threshold}`);
  }
  if (typeof cfg.check_pii === 'boolean') parts.push(`check_pii=${cfg.check_pii}`);
  if (typeof cfg.check_gender === 'boolean') parts.push(`check_gender=${cfg.check_gender}`);
  if (typeof cfg.ai_judge_enabled === 'boolean') parts.push(`ai_judge_enabled=${cfg.ai_judge_enabled}`);
  if (typeof cfg.failure_message === 'string' && cfg.failure_message.trim()) {
    parts.push(`message="${cfg.failure_message.trim()}"`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No additional config for this tier.';
}

/**
 * Heuristic that maps a Mitek `classification.imageType` (e.g.
 * `DriversLicenseFront`, `Selfie`, `IdDocumentBack`) to the IV upload audit
 * `image_type` (`id_front` / `id_back` / `selfie`). The dossier's `imageId`
 * is a Mitek UUID, not our DB id, so we can't join directly — image_type is
 * the closest stable join key.
 */
function ivAuditTypeForMitekImage(
  mitek: MitekImageProcessing,
): IdentityLookupUploadedImage['image_type'] | null {
  const raw = (mitek.classification?.imageType ?? '').toLowerCase();
  if (!raw) return null;
  if (raw.includes('back')) return 'id_back';
  if (raw.includes('front') || raw.includes('id') || raw.includes('document')) {
    return 'id_front';
  }
  if (raw.includes('selfie') || raw.includes('biometric') || raw.includes('face')) {
    return 'selfie';
  }
  return null;
}

/**
 * Pick the upload audit row that best matches a Mitek per-image entry by
 * `image_type` (matched via `ivAuditTypeForMitekImage`). Falls through in the
 * order the operator would expect: ID front, then back, then first selfie.
 */
function pickAuditForMitekImage(
  mitek: MitekImageProcessing,
  uploads: IdentityLookupUploadedImage[],
  alreadyUsed: Set<string>,
): IdentityLookupUploadedImage | null {
  if (uploads.length === 0) return null;
  const target = ivAuditTypeForMitekImage(mitek);
  if (target) {
    const candidate = uploads
      .filter((u) => u.image_type === target && !alreadyUsed.has(u.id))
      .sort((a, b) => a.sequence_index - b.sequence_index)[0];
    if (candidate) return candidate;
  }
  // No type hint — return the first un-used upload so the operator at least
  // sees something rather than text-only metadata.
  const fallback = uploads.find((u) => !alreadyUsed.has(u.id)) ?? null;
  return fallback;
}

/**
 * Authenticated thumbnail. Prefers short-lived Azure SAS (`preview_url` or
 * access-url mint) so the browser loads blob storage directly; falls back to
 * the authenticated content proxy for filesystem / mint failures.
 */
export function UploadedImagePreview({
  image,
  size = 96,
  onClick,
}: {
  image: IdentityLookupUploadedImage;
  size?: number;
  onClick?: (objectUrl: string, contentType: string) => void;
}) {
  const theme = useTheme();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; url: string; contentType: string; revokeOnCleanup: boolean }
    | { kind: 'pdf'; url: string; revokeOnCleanup: boolean }
    | { kind: 'error'; reason: string }
    | { kind: 's3' }
  >({ kind: 'loading' });

  useEffect(() => {
    if (image.storage_kind === 's3') {
      setState({ kind: 's3' });
      return;
    }
    if (image.storage_kind !== 'filesystem' && !isObjectStorageKind(image.storage_kind)) {
      setState({ kind: 's3' });
      return;
    }
    let cancelled = false;
    let revokeUrl: string | null = null;
    setState({ kind: 'loading' });

    const load = async () => {
      const previewOk =
        Boolean(image.preview_url) &&
        (!image.preview_url_expires_at ||
          Date.parse(image.preview_url_expires_at) - Date.now() > 30_000);

      if (isObjectStorageKind(image.storage_kind) && previewOk && image.preview_url) {
        return {
          url: image.preview_url,
          contentType: (image.content_type || 'image/jpeg').toLowerCase(),
          revokeOnCleanup: false,
        };
      }

      if (isObjectStorageKind(image.storage_kind)) {
        try {
          const access = await identityVerificationDashboardApi.uploadedImageAccessUrl(image.id);
          return {
            url: access.url,
            contentType: (access.contentType || image.content_type || 'image/jpeg').toLowerCase(),
            revokeOnCleanup: false,
          };
        } catch {
          // fall through to content proxy
        }
      }

      const blob = await identityVerificationDashboardApi.uploadedImageContent(image.id);
      return {
        url: URL.createObjectURL(blob),
        contentType: (blob.type || image.content_type || '').toLowerCase(),
        revokeOnCleanup: true,
      };
    };

    load()
      .then((resolved) => {
        if (cancelled) {
          if (resolved.revokeOnCleanup) URL.revokeObjectURL(resolved.url);
          return;
        }
        if (resolved.revokeOnCleanup) revokeUrl = resolved.url;
        if (resolved.contentType.includes('pdf')) {
          setState({ kind: 'pdf', url: resolved.url, revokeOnCleanup: resolved.revokeOnCleanup });
        } else {
          setState({
            kind: 'ready',
            url: resolved.url,
            contentType: resolved.contentType,
            revokeOnCleanup: resolved.revokeOnCleanup,
          });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Could not load image.';
        setState({ kind: 'error', reason: msg });
      });
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [
    image.id,
    image.storage_kind,
    image.content_type,
    image.preview_url,
    image.preview_url_expires_at,
  ]);

  const containerSx = {
    width: size,
    height: size,
    borderRadius: 1.5,
    border: 1,
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    bgcolor: alpha(theme.palette.text.primary, 0.04),
    cursor: state.kind === 'ready' && onClick ? 'zoom-in' : 'default',
  };

  if (state.kind === 'loading') {
    return (
      <Box sx={containerSx}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (state.kind === 's3') {
    return (
      <Tooltip title="This storage backend is not previewable from admin (S3 or unknown)." arrow>
        <Box sx={containerSx}>
          <Stack alignItems="center" spacing={0.25}>
            <BrokenImageRoundedIcon fontSize="small" color="disabled" />
            <Typography variant="caption" color="text.secondary">
              N/A
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  }
  if (state.kind === 'error') {
    return (
      <Tooltip title={state.reason} arrow>
        <Box sx={containerSx}>
          <Stack alignItems="center" spacing={0.25}>
            <BrokenImageRoundedIcon fontSize="small" color="error" />
            <Typography variant="caption" color="error">
              fail
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  }
  if (state.kind === 'pdf') {
    return (
      <Tooltip title="PDF — open in new tab" arrow>
        <Box
          sx={{ ...containerSx, cursor: 'pointer' }}
          onClick={() => window.open(state.url, '_blank', 'noopener,noreferrer')}
        >
          <Stack alignItems="center" spacing={0.25}>
            <OpenInNewRoundedIcon fontSize="small" />
            <Typography variant="caption">PDF</Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box sx={containerSx} onClick={() => onClick && onClick(state.url, state.contentType)}>
      <Box
        component="img"
        src={state.url}
        alt={`${image.image_type} #${image.sequence_index}`}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
}

export function ImagePreviewDialog({
  zoom,
  onClose,
}: {
  zoom: { image: IdentityLookupUploadedImage; url: string; contentType: string } | null;
  onClose: () => void;
}) {
  if (!zoom) return null;
  return (
    <Box
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.85)',
        zIndex: (t) => t.zIndex.modal + 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        cursor: 'zoom-out',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component="img"
          src={zoom.url}
          alt={`${zoom.image.image_type} #${zoom.image.sequence_index}`}
          sx={{
            maxWidth: '95vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
            cursor: 'default',
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'common.white' }}>
          <Chip
            size="small"
            color={
              zoom.image.image_type === 'selfie'
                ? 'primary'
                : zoom.image.image_type === 'id_front'
                  ? 'success'
                  : 'warning'
            }
            label={`${zoom.image.image_type} #${zoom.image.sequence_index}`}
            sx={{ fontWeight: 700 }}
          />
          <Typography variant="caption" sx={{ color: 'common.white' }}>
            {zoom.contentType} · {formatBytes(zoom.image.size_bytes)}
          </Typography>
          <Button
            component="a"
            href={zoom.url}
            download={
              zoom.image.original_filename ?? `${zoom.image.image_type}-${zoom.image.sequence_index}`
            }
            size="small"
            variant="contained"
            startIcon={<DownloadRoundedIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}
            onClick={(e) => e.stopPropagation()}
          >
            Download
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'common.white',
              borderColor: 'rgba(255,255,255,0.5)',
            }}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export function LookupSessionCard({
  row,
  policyTiers,
  variant = 'lookup',
}: {
  row: IdentityLookupRow;
  policyTiers: DedupPolicyTier[];
  variant?: 'lookup' | 'applicationDetail';
}) {
  const theme = useTheme();
  const dossier = row.dossier;
  const isApplicationDetail = variant === 'applicationDetail';
  const [zoom, setZoom] = useState<{
    image: IdentityLookupUploadedImage;
    url: string;
    contentType: string;
  } | null>(null);
  // Track which audit rows the per-image processing card has already used so
  // we don't show the same id_front image next to two different Mitek rows.
  const usedAuditIds = new Set<string>();

  const sessionStatusColor = identitySessionStatusColor(row.session_status);

  const mitekChip = (
    <Chip
      size="small"
      color={
        row.mitekCalled && dossier?.findings.authenticated === true
          ? 'success'
          : row.mitekCalled && dossier?.findings.authenticated === false
            ? 'error'
            : row.mitekCalled
              ? 'warning'
              : 'default'
      }
      variant={row.mitekCalled ? 'filled' : 'outlined'}
      icon={
        row.mitekCalled && dossier?.findings.authenticated === true ? (
          <CheckCircleRoundedIcon fontSize="small" />
        ) : row.mitekCalled && dossier?.findings.authenticated === false ? (
          <CancelRoundedIcon fontSize="small" />
        ) : (
          <InfoOutlinedIcon fontSize="small" />
        )
      }
      label={
        row.mitekCalled
          ? `Mitek: ${
              dossier?.findings.authenticated === true
                ? 'AUTHENTICATED'
                : dossier?.findings.authenticated === false
                  ? 'NOT AUTHENTICATED'
                  : 'INDETERMINATE'
            } · ${probabilityToPercent(dossier?.findings.probability ?? null)}`
          : 'Mitek not called'
      }
      sx={{ fontWeight: 700 }}
    />
  );

  const documentLocale = [row.document_type, row.issuing_country, row.issuing_province].filter(Boolean).join(' · ');

  return (
    <Card
      variant="outlined"
      elevation={isApplicationDetail ? 0 : undefined}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.18 : 0.32),
        ...(isApplicationDetail ? { boxShadow: theme.shadows[1] } : {}),
      }}
    >
      {isApplicationDetail ? <Box sx={{ height: 4, background: GEMINI_GRADIENT }} /> : null}
      <CardContent sx={{ pt: isApplicationDetail ? 2.5 : undefined }}>
        {isApplicationDetail ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
              <Chip size="small" label={row.session_status} color={sessionStatusColor} sx={{ fontWeight: 700 }} />
              <Chip size="small" variant="outlined" label={row.verification_route ?? 'no-route'} sx={{ fontWeight: 600 }} />
              <Chip size="small" variant="outlined" label={`vendor: ${row.vendor}`} sx={{ fontWeight: 600 }} />
              {row.ccid ? (
                <Chip size="small" color="primary" variant="outlined" label={`CCID ${row.ccid}`} sx={{ fontWeight: 700 }} />
              ) : (
                <Chip size="small" variant="outlined" label="no ccid" sx={{ fontWeight: 600 }} />
              )}
            </Stack>
            <Box sx={VERIFICATION_GRID_SX}>
              <VerificationDetailField label="Session status">
                <Chip size="small" label={row.session_status} color={sessionStatusColor} sx={{ fontWeight: 700 }} />
              </VerificationDetailField>
              <VerificationDetailField label="Route">
                <Typography variant="body2">{row.verification_route ?? '—'}</Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Vendor">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.vendor}
                </Typography>
              </VerificationDetailField>
              <VerificationDetailField label="CCID">
                <Typography variant="body2" sx={{ fontWeight: row.ccid ? 600 : 400 }}>
                  {row.ccid ?? '—'}
                </Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Email">
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {row.email ?? '—'}
                </Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Phone">
                <Typography variant="body2">{row.phone_number ?? '—'}</Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Document">
                <Typography variant="body2">{documentLocale || '—'}</Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Created">
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(row.created_at)}
                </Typography>
              </VerificationDetailField>
              <VerificationDetailField label="Internal ID">
                <Typography sx={monoValueSx(theme)}>{row.internal_id}</Typography>
              </VerificationDetailField>
              <VerificationDetailField label="IV session ID" fullWidth>
                <Typography sx={monoValueSx(theme)}>{row.iv_verification_id}</Typography>
              </VerificationDetailField>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexWrap="wrap"
              useFlexGap
            >
              {mitekChip}
              {dossier ? (
                <Typography variant="caption" color="text.secondary">
                  Pass {dossier.findings.passCount} · Undetermined {dossier.findings.undeterminedCount} · Fail{' '}
                  {dossier.findings.failCount}
                  {dossier.findings.otherCount > 0 ? ` · Other ${dossier.findings.otherCount}` : ''}
                </Typography>
              ) : null}
              {row.evidenceProcessingPrimary?.primaryMessage ? (
                <Tooltip title={row.evidenceProcessingPrimary.primaryMessage} placement="top" arrow>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Image processing code ${row.evidenceProcessingPrimary.primaryCode ?? '?'}`}
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                <Chip
                  size="small"
                  label={row.session_status}
                  color={sessionStatusColor}
                  sx={{ fontWeight: 700 }}
                />
                <Chip size="small" variant="outlined" label={row.verification_route ?? 'no-route'} sx={{ fontWeight: 600 }} />
                <Chip size="small" variant="outlined" label={`vendor: ${row.vendor}`} sx={{ fontWeight: 600 }} />
                {row.ccid ? (
                  <Chip size="small" color="primary" variant="outlined" label={`CCID ${row.ccid}`} sx={{ fontWeight: 700 }} />
                ) : (
                  <Chip size="small" variant="outlined" label="no ccid" sx={{ fontWeight: 600 }} />
                )}
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5, ...monoValueSx(theme) }}>
                {row.iv_verification_id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                internal_id {row.internal_id} · created {formatDateTime(row.created_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {row.email ?? 'no email'} · {row.phone_number ?? 'no phone'} ·{' '}
                {row.document_type ?? 'no document'}{' '}
                {[row.issuing_country, row.issuing_province].filter(Boolean).join(' · ') || ''}
              </Typography>
            </Box>
            <Stack spacing={0.75} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              {mitekChip}
              {dossier ? (
                <Typography variant="caption" color="text.secondary">
                  Pass {dossier.findings.passCount} · Undetermined {dossier.findings.undeterminedCount} · Fail{' '}
                  {dossier.findings.failCount}
                  {dossier.findings.otherCount > 0 ? ` · Other ${dossier.findings.otherCount}` : ''}
                </Typography>
              ) : null}
              {row.evidenceProcessingPrimary?.primaryMessage ? (
                <Tooltip title={row.evidenceProcessingPrimary.primaryMessage} placement="left" arrow>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Image processing code ${row.evidenceProcessingPrimary.primaryCode ?? '?'}`}
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        )}

        {row.biometricMatchComparison ? (
          <Box sx={{ mt: 2 }}>
            <BiometricMatchComparisonPanel
              comparison={row.biometricMatchComparison}
              onZoom={setZoom}
            />
          </Box>
        ) : null}

        {!row.mitekCalled ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Automated photo and ID check was not run for this session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The system did not send this attempt to the document vendor, so there is no pass/fail score to
                show here. You can still review uploaded photos, text read from the ID (OCR), and duplicate
                checks in the <strong>Operations</strong> section below.
              </Typography>
            </Alert>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 1,
                '&.Mui-expanded': { mt: 1 },
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 48, px: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Technical details (for support staff)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>raw_idv_response</strong> is empty — the vendor JSON blob was never saved for this
                    session, which usually means the Mitek step was skipped or not reached.
                  </Typography>
                  {row.verification_route ? (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Verification route:</strong>{' '}
                      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {row.verification_route}
                      </Box>
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Vendor on session:</strong> {row.vendor}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}

        {!dossier && row.uploadedImages.length > 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Uploaded files
              </Typography>
              <Stack
                direction="row"
                spacing={1.25}
                sx={{ flexWrap: 'wrap', rowGap: 1.25 }}
                useFlexGap
              >
                {row.uploadedImages.map((u) => (
                  <Stack key={u.id} spacing={0.5} alignItems="center" sx={{ width: 110 }}>
                    <UploadedImagePreview
                      image={u}
                      size={110}
                      onClick={(url, contentType) =>
                        setZoom({ image: u, url, contentType })
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        u.image_type === 'selfie'
                          ? 'primary'
                          : u.image_type === 'id_front'
                            ? 'success'
                            : 'warning'
                      }
                      label={`${u.image_type} #${u.sequence_index}`}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {dossier ? (
          <MitekDossierPanels
            dossier={dossier}
            row={row}
            usedAuditIds={usedAuditIds}
            setZoom={setZoom}
            variant={isApplicationDetail ? 'applicationDetail' : 'lookup'}
          />
        ) : null}

        {(row.latestDedup || row.customer) && (
          <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
        )}

        <Grid container spacing={2}>
          {row.latestDedup ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Latest deduplication attempt
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={row.latestDedup.status}
                      color={
                        row.latestDedup.isDuplicate === true ||
                        row.latestDedup.status === 'REJECTED' ||
                        row.latestDedup.status === 'REJECTED_DUPLICATE'
                          ? 'error'
                          : row.latestDedup.isDuplicate === false ||
                              row.latestDedup.status === 'SUCCESS' ||
                              row.latestDedup.status === 'COMPLETED'
                            ? 'success'
                            : 'default'
                      }
                      sx={{ fontWeight: 700 }}
                    />
                    {row.latestDedup.matched_tiers.length > 0 ? (
                      row.latestDedup.matched_tiers.map((tier) => (
                        <Chip key={tier} size="small" color="error" variant="outlined" label={`hit ${tier}`} />
                      ))
                    ) : (
                      <Chip size="small" variant="outlined" label="no tier hit" />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                    dv_verification_id {row.latestDedup.dv_verification_id}
                  </Typography>
                  {row.latestDedup.message ? (
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      {row.latestDedup.message}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    score <strong>{row.latestDedup.score ?? '—'}</strong> / threshold{' '}
                    <strong>{row.latestDedup.threshold ?? '—'}</strong>
                  </Typography>
                  {policyTiers.length > 0 ? (
                    <Box sx={{ mt: 1.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Tier matrix (from deduplication policy)
                      </Typography>
                      <Stack spacing={0.6} sx={{ mt: 0.6 }}>
                        {policyTiers.map((tier) => {
                          const evalRow = row.latestDedup?.tier_evaluation?.find(
                            (t) => t.tier === tier.tier,
                          );
                          const hit = evalRow?.hit ?? row.latestDedup?.matched_tiers.includes(tier.tier);
                          return (
                            <Stack key={tier.tier} direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                size="small"
                                label={hit ? 'HIT' : 'CLEAR'}
                                color={hit ? 'error' : 'success'}
                                variant={hit ? 'filled' : 'outlined'}
                                sx={{ minWidth: 68, fontWeight: 700 }}
                              />
                              <Tooltip title={tierDetailText(tier)} arrow placement="top">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    borderBottom: '1px dashed',
                                    borderColor: 'divider',
                                    cursor: 'help',
                                  }}
                                >
                                  {tier.tier.toUpperCase()} · {tier.label}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ) : null}
          {row.customer ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Customer profile (success branch)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color="success" label={`status ${row.customer.status}`} sx={{ fontWeight: 700 }} />
                    <Chip size="small" variant="outlined" label={`CCID ${row.customer.ccid}`} sx={{ fontWeight: 600 }} />
                  </Stack>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">
                        {[row.customer.first_name, row.customer.last_name].filter(Boolean).join(' ') || '—'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        DOB
                      </Typography>
                      <Typography variant="body2">{row.customer.date_of_birth ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">{row.customer.email ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">{row.customer.phone_number ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {row.customer.residential_address ?? '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
      <ImagePreviewDialog zoom={zoom} onClose={() => setZoom(null)} />
    </Card>
  );
}

export function IdentityVerificationLookup() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const policyQ = useQuery({
    queryKey: ['identityVerificationDedupPolicyForLookup'],
    queryFn: () => identityVerificationDashboardApi.policy(),
  });

  const lookup = useMutation({
    mutationKey: ['identityVerificationLookup'],
    mutationFn: () =>
      identityVerificationDashboardApi.lookup({
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      }),
  });

  const submit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email.trim() && !phone.trim()) return;
    lookup.mutate();
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
            Lookup IDV records by contact
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Provide email and/or phone — operator gets every IV session for that contact along with the
            parsed Mitek dossier (verifications passed, values missed, image-level processing reasons).
          </Typography>
          <Box component="form" onSubmit={submit}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email contains"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone contains"
                  placeholder="+1 416 ..."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<SearchRoundedIcon />}
                  disabled={(!email.trim() && !phone.trim()) || lookup.isPending}
                  sx={{ fontWeight: 800, textTransform: 'none' }}
                >
                  {lookup.isPending ? 'Searching…' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          {lookup.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {messageFromIdentityVerificationHttpError(lookup.error)}
            </Alert>
          ) : null}
          {lookup.data ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {lookup.data.items.length} record(s) for{' '}
              {lookup.data.criteria.email ? <strong>email "{lookup.data.criteria.email}"</strong> : null}
              {lookup.data.criteria.email && lookup.data.criteria.phone ? ' and ' : null}
              {lookup.data.criteria.phone ? <strong>phone "{lookup.data.criteria.phone}"</strong> : null}.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {lookup.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} />
        </Box>
      ) : null}

      {lookup.data && lookup.data.items.length === 0 ? (
        <Alert severity="info">
          No identity verification sessions found for the supplied contact details.
        </Alert>
      ) : null}

      {lookup.data && lookup.data.items.length > 0 ? (
        <Stack spacing={2}>
          {lookup.data.items.map((row) => (
            <LookupSessionCard
              key={row.iv_verification_id}
              row={row}
              policyTiers={policyQ.data?.tiers ?? []}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
